import { memo } from 'react';
import { Box, Text, Badge, VStack, HStack, Tooltip, Icon, useToast, keyframes, Divider } from '@chakra-ui/react';
import ProdutoImagem from "../ProdutoImagem";
import {
    formatBRL, formatShortDate, formatDeadlineShort,
    MONEY_OWNER_META, URGENCY_META,
    getStatusDevolucaoLabel, getStatusDevolucaoColor,
    getStatusReclamacaoLabel, getStatusReclamacaoColor,
    STATUS_DEVOLUCAO_META, STATUS_RECLAMACAO_META,
} from '../../utils/formatters';
import {
    FaClock, FaArrowDown, FaLock, FaHandHoldingUsd, FaQuestionCircle,
    FaUser, FaPaperclip, FaExclamationCircle, FaRobot, FaFlag,
} from 'react-icons/fa';
import "dayjs/locale/pt-br";

const urgentPulse = keyframes`
    0%   { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.55); border-color: #E53E3E; }
    70%  { box-shadow: 0 0 0 8px rgba(229, 62, 62, 0);  border-color: #FC8181; }
    100% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0);    border-color: #E53E3E; }
`;

const MONEY_ICONS = {
    buyer:   FaArrowDown,
    ml:      FaLock,
    seller:  FaHandHoldingUsd,
    unknown: FaQuestionCircle,
};

const dataLonga = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR') : '');

/**
 * Data do marco da coluna em que o card está: quando entrou em Finalizada,
 * em Recebida ou quando o card foi criado. Cai para a data da devolução
 * quando o marco não existe (registros anteriores ao histórico).
 */
const marcoDaColuna = (devolucao) => {
    const finalizada = devolucao.finalizada === 1 || devolucao.estoque === 1 || devolucao.avaria === 1;

    if (finalizada && devolucao.finalizada_at) {
        return {
            prefixo: 'finalizada',
            texto: formatShortDate(devolucao.finalizada_at),
            tooltip: `Finalizada em ${dataLonga(devolucao.finalizada_at)}`,
        };
    }

    if (devolucao.received === 1 && devolucao.recebida_at) {
        return {
            prefixo: 'recebida',
            texto: formatShortDate(devolucao.recebida_at),
            tooltip: `Recebida em ${dataLonga(devolucao.recebida_at)}`,
        };
    }

    if (!finalizada && devolucao.received !== 1 && devolucao.created_at) {
        return {
            prefixo: 'na fila desde',
            texto: formatShortDate(devolucao.created_at),
            tooltip: `Entrou na fila em ${dataLonga(devolucao.created_at)}`,
        };
    }

    return {
        prefixo: '',
        texto: formatShortDate(devolucao.date_created),
        tooltip: 'Data da devolução',
    };
};

const MiniLegenda = ({ children }) => (
    <Text
        fontSize={{ base: '7px', md: '8px' }}
        color="gray.400"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="0.03em"
        lineHeight="1"
        mb="1px"
    >
        {children}
    </Text>
);

const StatusBadge = ({ status }) => (
    <VStack spacing={0} align="center">
        <MiniLegenda>Devolução</MiniLegenda>
        <Tooltip
            label={`Devolução: ${STATUS_DEVOLUCAO_META[status]?.description || 'Situação do retorno do produto.'}`}
            placement="top"
            hasArrow
            openDelay={300}
        >
            <Badge
                colorScheme={getStatusDevolucaoColor(status)}
                fontSize={{ base: '9px', md: '0.65em' }}
                px={{ base: 1.5, md: 2 }}
                borderRadius="md"
                variant="solid"
                lineHeight="1.2"
            >
                {getStatusDevolucaoLabel(status)}
            </Badge>
        </Tooltip>
    </VStack>
);

const ReclamacaoBadge = ({ status }) => {
    if (!status) return null;

    return (
        <VStack spacing={0} align="center">
            <MiniLegenda>Reclamação</MiniLegenda>
            <Tooltip
                label={`Reclamação: ${STATUS_RECLAMACAO_META[status]?.description || 'Situação da reclamação no Mercado Livre.'}`}
                placement="top"
                hasArrow
                openDelay={300}
            >
                <Badge
                    colorScheme={getStatusReclamacaoColor(status)}
                    fontSize={{ base: '9px', md: '0.65em' }}
                    px={{ base: 1.5, md: 2 }}
                    borderRadius="md"
                    variant="outline"
                    lineHeight="1.2"
                >
                    {getStatusReclamacaoLabel(status)}
                </Badge>
            </Tooltip>
        </VStack>
    );
};

const DeadlineBadge = ({ urgency, dueUtil, titleMl }) => {
    if (!urgency || urgency === 'sem_prazo') return null;
    const meta = URGENCY_META[urgency] || URGENCY_META.sem_prazo;
    const when = formatDeadlineShort(dueUtil, '');
    const label = urgency === 'urgente'
        ? 'Revisar HOJE'
        : urgency === 'atrasada'
            ? `Atrasada${when ? ` (${when})` : ''}`
            : when
                ? `Revisar ${when}`
                : meta.label;
    const tooltipText = titleMl ? `${meta.description} (ML: "${titleMl}")` : meta.description;

    return (
        <Tooltip label={tooltipText} hasArrow>
            <Badge
                colorScheme={meta.color}
                fontSize={{ base: '9px', md: '10px' }}
                px={{ base: 1.5, md: 2 }}
                py={0.5}
                borderRadius="md"
                variant={urgency === 'urgente' || urgency === 'atrasada' ? 'solid' : 'subtle'}
                display="inline-flex"
                alignItems="center"
                gap="4px"
                lineHeight="1.2"
            >
                <FaClock size={9} /> {label}
            </Badge>
        </Tooltip>
    );
};

const MoneyBadge = ({ owner, total }) => {
    if (!owner) return null;
    const m = MONEY_OWNER_META[owner] || MONEY_OWNER_META.unknown;
    const IconComp = MONEY_ICONS[owner] || FaQuestionCircle;
    const shortMap = { buyer: 'Devolvido', ml: 'Retido ML', seller: 'Recuperado', unknown: 'Indef.' };
    const short = shortMap[owner] || 'Indef.';
    const label = (owner === 'buyer' && Number(total) > 0)
        ? formatBRL(total)
        : short;
    return (
        <Tooltip label={m.description} hasArrow>
            <Badge
                colorScheme={m.color}
                fontSize={{ base: '9px', md: '10px' }}
                px={{ base: 1.5, md: 2 }}
                borderRadius="md"
                variant="subtle"
                lineHeight="1.3"
                display="inline-flex"
                alignItems="center"
                gap="4px"
            >
                <Icon as={IconComp} boxSize="10px" /> {label}
            </Badge>
        </Tooltip>
    );
};

const AnexosBadge = ({ arquivos }) => {
    const count = Array.isArray(arquivos) ? arquivos.length : 0;
    const hasFiles = count > 0;
    return (
        <Tooltip label={hasFiles ? `${count} arquivo${count === 1 ? '' : 's'} anexado${count === 1 ? '' : 's'}` : 'Sem imagens anexadas'} hasArrow>
            <Badge
                colorScheme={hasFiles ? 'blue' : 'gray'}
                variant={hasFiles ? 'solid' : 'outline'}
                fontSize="10px"
                px={1.5}
                borderRadius="md"
                display="inline-flex"
                alignItems="center"
                gap="3px"
                opacity={hasFiles ? 1 : 0.6}
            >
                <Icon as={FaPaperclip} boxSize="9px" /> {count}
            </Badge>
        </Tooltip>
    );
};

export const hasIaAnalise = (preAnalise) =>
    !!(preAnalise && preAnalise.includes('ANÁLISE AUTOMÁTICA DE DEFEITOS'));

const IaBadge = ({ preAnalise }) => {
    const analisada = hasIaAnalise(preAnalise);
    return (
        <Tooltip label={analisada ? 'Análise de IA disponível' : 'IA ainda não analisou as imagens'} hasArrow>
            <Badge
                colorScheme={analisada ? 'teal' : 'gray'}
                variant={analisada ? 'solid' : 'outline'}
                fontSize="10px"
                px={1.5}
                borderRadius="md"
                display="inline-flex"
                alignItems="center"
                gap="3px"
                opacity={analisada ? 1 : 0.6}
            >
                <Icon as={FaRobot} boxSize="9px" /> IA
            </Badge>
        </Tooltip>
    );
};

const copyToClipboard = async (value, toast, label) => {
    if (!value) return;
    try {
        await navigator.clipboard.writeText(String(value));
        toast({
            title: `${label} copiado`,
            description: String(value),
            status: 'success',
            duration: 1800,
            isClosable: true,
            position: 'top-right',
        });
    } catch (e) {
        toast({ title: 'Falha ao copiar', status: 'error', duration: 2000 });
    }
};

const IdChipFooter = ({ value, tooltip, onCopy }) => {
    if (!value) return null;
    const full = String(value);
    const display = full.length > 16 ? '…' + full.slice(-14) : full;
    return (
        <Tooltip label={`${tooltip} · clique para copiar`} hasArrow>
            <Text
                fontSize="10px"
                fontFamily="mono"
                color="gray.600"
                noOfLines={1}
                cursor="pointer"
                _hover={{ color: 'orange.600' }}
                onClick={(e) => { e.stopPropagation(); onCopy(); }}
            >
                {display}
            </Text>
        </Tooltip>
    );
};

const KanbanReturnCard = ({ devolucao, onClick }) => {
    const toast = useToast();

    const item = devolucao.orders?.[0] || {};
    const orderId = item.order_id || devolucao.id_order_ml;
    const idOrigem = devolucao.id_origem || orderId;
    const headerId = idOrigem || devolucao.id_venda;
    const headerLabel = idOrigem ? 'ID Origem' : 'ID TAGA';
    const title = item.nome || 'Título do Produto Não Encontrado';
    const sku = item.sku || '';
    const codigoGlobal = item.codigo_global || '';
    const codigoFornecedor = item.codigo_fornecedor || '';
    const precoTotal = Number(item.preco_total || 0);

    const cliente = devolucao.dados_cliente || {};
    const clienteNome = cliente.first_name
        ? `${cliente.first_name}${cliente.last_name ? ' ' + cliente.last_name : ''}`
        : (cliente.business_name || '');

    const adicional = devolucao.dados_adicionais || {};
    const motivo = adicional.problem || adicional.description || '';
    const resolucao = devolucao.review_title_ml || adicional.title || '';
    const mostrarResolucao = resolucao && resolucao !== motivo;

    const marco = marcoDaColuna(devolucao);
    const urgency = devolucao.urgency_level || 'sem_prazo';
    const isUrgent = urgency === 'urgente' || urgency === 'atrasada';
    const isHojePulsing = urgency === 'urgente';
    const origem = (devolucao.origem || 'ml').toLowerCase();

    return (
        <Box
            onClick={() => onClick && onClick(devolucao)}
            bg={isUrgent ? 'red.50' : 'white'}
            boxShadow="lg"
            borderRadius="lg"
            p={{ base: 2.5, md: 3 }}
            _hover={{ boxShadow: "xl", cursor: "pointer", transform: "translateY(-2px)" }}
            borderWidth={isHojePulsing ? '2px' : '1px'}
            borderColor={isUrgent ? 'red.400' : 'gray.300'}
            w="100%"
            transition="all 0.2s ease-in-out"
            animation={isHojePulsing ? `${urgentPulse} 1.6s ease-in-out infinite` : undefined}
        >
            <VStack align="stretch" spacing={1.5}>
                <HStack justify="space-between" align="flex-end" spacing={1}>
                    <Tooltip label={`${headerLabel}: ${headerId} · clique para copiar`} hasArrow>
                        <Text
                            fontSize="11px"
                            fontWeight="bold"
                            color="gray.700"
                            noOfLines={1}
                            cursor="pointer"
                            _hover={{ color: 'orange.600' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(headerId, toast, headerLabel);
                            }}
                        >
                            #{headerId}
                        </Text>
                    </Tooltip>
                    <HStack spacing={1} flexShrink={0}>
                        <ReclamacaoBadge status={devolucao.status_reclamacao} />
                        <StatusBadge status={devolucao.status} />
                    </HStack>
                </HStack>

                <HStack spacing={1} flexWrap="wrap">
                    <DeadlineBadge
                        urgency={urgency}
                        dueUtil={devolucao.review_due_date_util}
                        titleMl={devolucao.review_title_ml}
                    />
                    {origem === 'ml' ? (
                        <Badge
                            colorScheme={String(devolucao.conta) === '2' ? 'blue' : String(devolucao.conta) === '3' ? 'green' : 'yellow'}
                            fontSize="9px" px={1.5} borderRadius="md" variant="solid"
                        >
                            ML {devolucao.conta ?? ''}
                        </Badge>
                    ) : (
                        <Badge colorScheme="purple" fontSize="9px" px={1.5} borderRadius="md" variant="solid">
                            {origem.toUpperCase()}
                        </Badge>
                    )}
                </HStack>

                <Divider borderColor="gray.400" />

                <HStack spacing={2} align="flex-start">
                    <Box w={{ base: "45px", md: "56px" }} h={{ base: "45px", md: "56px" }} flexShrink={0}>
                        <ProdutoImagem
                            produto={item}
                            origem={null}
                            width={{ base: "45px", md: "56px" }}
                            height={{ base: "45px", md: "56px" }}
                        />
                    </Box>
                    <VStack align="stretch" spacing={0.5} flex={1} minW={0}>
                        <Tooltip label={title}>
                            <Text
                                fontSize={{ base: "11px", md: "xs" }}
                                color="gray.800"
                                fontWeight="semibold"
                                noOfLines={2}
                                lineHeight="1.3"
                            >
                                {title}
                            </Text>
                        </Tooltip>
                        {motivo && (
                            <Tooltip label={motivo}>
                                <HStack spacing={1} align="center" color="red.600">
                                    <Icon as={FaExclamationCircle} boxSize="10px" flexShrink={0} />
                                    <Text fontSize="10px" noOfLines={1} fontStyle="italic">{motivo}</Text>
                                </HStack>
                            </Tooltip>
                        )}
                        {mostrarResolucao && (
                            <Tooltip label={resolucao}>
                                <HStack spacing={1} align="center" color="gray.500">
                                    <Icon as={FaFlag} boxSize="9px" flexShrink={0} />
                                    <Text fontSize="10px" noOfLines={1}>{resolucao}</Text>
                                </HStack>
                            </Tooltip>
                        )}
                    </VStack>
                </HStack>

                <HStack justify="space-between" align="center" spacing={2}>
                    <HStack spacing={2} minW={0} flex={1} align="center">
                        {(sku || codigoGlobal || codigoFornecedor) && (
                            <Text fontSize="10px" color="gray.500" fontWeight="semibold" flexShrink={0}>
                                Ref. Item:
                            </Text>
                        )}
                        <HStack spacing={2} minW={0} flex={1} align="center" divider={<Text color="gray.300" fontSize="10px">|</Text>}>
                        {sku && (
                            <Tooltip label={`SKU: ${sku} · clique para copiar`} hasArrow>
                                <Text
                                    fontSize="11px" fontWeight="semibold" color="gray.700"
                                    noOfLines={1} cursor="pointer" flexShrink={0}
                                    _hover={{ color: 'orange.600' }}
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(sku, toast, 'SKU'); }}
                                >
                                    {sku}
                                </Text>
                            </Tooltip>
                        )}
                        {codigoGlobal && (
                            <Tooltip label={`Cód. Global: ${codigoGlobal} · clique para copiar`} hasArrow>
                                <Text
                                    fontSize="10px" color="gray.500" noOfLines={1} cursor="pointer"
                                    _hover={{ color: 'orange.600' }}
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(codigoGlobal, toast, 'Cód. Global'); }}
                                >
                                    {codigoGlobal}
                                </Text>
                            </Tooltip>
                        )}
                        {codigoFornecedor && (
                            <Tooltip label={`Cód. Fornecedor: ${codigoFornecedor} · clique para copiar`} hasArrow>
                                <Text
                                    fontSize="10px" color="gray.500" noOfLines={1} cursor="pointer"
                                    _hover={{ color: 'orange.600' }}
                                    onClick={(e) => { e.stopPropagation(); copyToClipboard(codigoFornecedor, toast, 'Cód. Fornecedor'); }}
                                >
                                    {codigoFornecedor}
                                </Text>
                            </Tooltip>
                        )}
                        </HStack>
                    </HStack>
                    {precoTotal > 0 && (
                        <Text fontSize="13px" fontWeight="bold" color="gray.800" flexShrink={0}>
                            {formatBRL(precoTotal)}
                        </Text>
                    )}
                </HStack>

                {clienteNome && (
                    <Tooltip label={clienteNome}>
                        <HStack spacing={1} color="gray.600">
                            <Icon as={FaUser} boxSize="9px" flexShrink={0} color="gray.500" />
                            <Text fontSize="11px" noOfLines={1} fontWeight="medium">{clienteNome}</Text>
                        </HStack>
                    </Tooltip>
                )}

                <Divider borderColor="gray.400" />

                <HStack wrap="wrap" spacing={1.5} rowGap={1.5}>
                    <MoneyBadge owner={devolucao.money_owner} total={devolucao.total_perdido} />
                    <AnexosBadge arquivos={devolucao.arquivos_anexados} />
                    <IaBadge preAnalise={devolucao.pre_analise} />
                    <Tooltip label={marco.tooltip} hasArrow>
                        <Text fontSize="10px" color="gray.500" ml="auto" whiteSpace="nowrap">
                            {marco.prefixo && (
                                <Text as="span" color="gray.400">{marco.prefixo} </Text>
                            )}
                            {marco.texto}
                        </Text>
                    </Tooltip>
                </HStack>

                {(devolucao.id_venda || orderId || devolucao.pack_id_ml) && (
                    <>
                    <HStack align="center" spacing={2}>
                        <Text fontSize="10px" color="gray.500" fontWeight="semibold" flexShrink={0}>
                            Ref. Venda:
                        </Text>
                        <HStack wrap="wrap" spacing={2} rowGap={1} divider={<Text color="gray.300" fontSize="10px">|</Text>}>
                            <IdChipFooter
                                value={devolucao.id_venda}
                                tooltip={`ID TAGA: ${devolucao.id_venda}`}
                                onCopy={() => copyToClipboard(devolucao.id_venda, toast, 'ID TAGA')}
                            />
                            {orderId && String(orderId) !== String(devolucao.id_venda) && (
                                <IdChipFooter
                                    value={orderId}
                                    tooltip={`ID Origem: ${orderId}`}
                                    onCopy={() => copyToClipboard(orderId, toast, 'ID Origem')}
                                />
                            )}
                            {devolucao.pack_id_ml && String(devolucao.pack_id_ml) !== String(orderId) && String(devolucao.pack_id_ml) !== String(devolucao.id_venda) && (
                                <IdChipFooter
                                    value={devolucao.pack_id_ml}
                                    tooltip={`Pack ID: ${devolucao.pack_id_ml}`}
                                    onCopy={() => copyToClipboard(devolucao.pack_id_ml, toast, 'Pack ID')}
                                />
                            )}
                        </HStack>
                    </HStack>
                    </>
                )}
            </VStack>
        </Box>
    );
};

export default memo(KanbanReturnCard);